import type { SubmitEvent } from 'react'
import { VpIcon, VpIconButton } from '@vtmn-play/react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface FavoriteViewTitleEditFormProps {
  viewId: string
  title: string
  onRename: (title: string) => Promise<boolean>
  onClose: (restoreFocus: boolean) => void
}

export function FavoriteViewTitleEditForm({ viewId, title, onRename, onClose }: Readonly<FavoriteViewTitleEditFormProps>) {
  const { t } = useTranslation()
  const [editedTitle, setEditedTitle] = useState(title)
  const [isSaving, setIsSaving] = useState(false)
  const titleInputRef = useRef<HTMLInputElement>(null)
  const titleFormRef = useRef<HTMLFormElement>(null)
  const submittedByEnterRef = useRef(false)

  useEffect(() => {
    titleInputRef.current?.focus()

    const handlePointerDown = (event: PointerEvent) => {
      if (!(event.target instanceof Node) || titleFormRef.current?.contains(event.target))
        return

      setEditedTitle(title)
      onClose(false)
    }

    document.addEventListener('pointerdown', handlePointerDown)
    return () => document.removeEventListener('pointerdown', handlePointerDown)
  }, [onClose, title])

  const cancelRename = () => {
    setEditedTitle(title)
    onClose(true)
  }

  const saveRename = async (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault()
    const submittedByEnter = submittedByEnterRef.current
    submittedByEnterRef.current = false
    const normalizedTitle = editedTitle.trim()
    if (!normalizedTitle || isSaving)
      return

    setIsSaving(true)
    try {
      if (await onRename(normalizedTitle))
        onClose(!submittedByEnter)
    }
    finally {
      setIsSaving(false)
    }
  }

  return (
    <form ref={titleFormRef} className="favorite-views-page__view-title-edit" onSubmit={saveRename}>
      <input
        ref={titleInputRef}
        maxLength={100}
        aria-label={t('favorite-views.management.rename-view')}
        value={editedTitle}
        onChange={event => setEditedTitle(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
            submittedByEnterRef.current = true
            return
          }

          if (event.key === 'Escape') {
            event.preventDefault()
            cancelRename()
          }
        }}
        onKeyUp={(event) => {
          if (event.key === 'Enter')
            submittedByEnterRef.current = false
        }}
        data-testid={`favorite-views-rename-input-${viewId}`}
      />
      <VpIconButton
        type="submit"
        className="favorite-views-page__view-title-save"
        variant="primary"
        size="small"
        disabled={!editedTitle.trim() || isSaving}
        aria-label={t('favorite-views.management.save-view-name')}
        data-testid={`favorite-views-save-title-${viewId}`}
      >
        <VpIcon name="check" />
      </VpIconButton>
    </form>
  )
}
